<template>
  <video
    ref="videoEl"
    class="media-video"
    :class="{'video-flipped': flipHorizontally, 'video-cover': fit === 'cover'}"
    autoplay
    playsinline
    :muted="isMuted"
  />
</template>

<script>
import { markRaw } from "vue";

export default {
  name: "MediaTrackRenderer",
  props: {
    videoTrack: {
      type: Object,
      default: null,
    },
    audioTrack: {
      type: Object,
      default: null,
    },
    stream: {
      type: Object,
      default: null,
    },
    isMuted: {
      type: Boolean,
      default: false,
    },
    fit: {
      type: String,
      required: false,
      default: "contain",
      validator: fit => ["contain", "cover"].includes(fit),
    },
    flipHorizontally: {
      type: Boolean,
      default: false,
    },
    sinkId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      boundTrack: null,
    };
  },
  watch: {
    videoTrack: "attach",
    audioTrack: "attach",
    stream: "attach",
    isMuted: "applyMuted",
    sinkId: "applySinkId",
  },
  mounted() {
    this.attach();
    this.applySinkId();
  },
  beforeUnmount() {
    this.detach();
  },
  methods: {
    attach() {
      const el = this.$refs.videoEl;
      if (!el) {
        return;
      }

      el.autoplay = true;
      el.playsInline = true;
      el.muted = Boolean(this.isMuted);

      const { videoTrack, audioTrack } = this;

      if (videoTrack && typeof videoTrack.attach === "function") {
        if (this.boundTrack && this.boundTrack !== videoTrack && typeof this.boundTrack.detach === "function") {
          try {
            this.boundTrack.detach(el);
          } catch (error) {
            // ignore detach errors
          }
        }

        // Always bind to the element Vue rendered. Writing back to $refs would
        // desynchronise Vue's bookkeeping from the vnode's real element.
        videoTrack.attach(el);

        this.boundTrack = markRaw(videoTrack);
        this.ensureAudioTrack(el, audioTrack);
        el.play?.().catch(() => {});
        return;
      }

      if (this.stream) {
        el.srcObject = this.stream;
        el.play?.().catch(() => {});
      }
    },
    ensureAudioTrack(el, audioTrack) {
      if (!el || this.isMuted || !audioTrack) {
        return;
      }
      const targetTrack = audioTrack.mediaStreamTrack || audioTrack;
      let stream = el.srcObject;
      if (!(stream instanceof MediaStream)) {
        stream = new MediaStream();
        el.srcObject = stream;
      }
      const hasTrack = stream.getAudioTracks().some(track => track === targetTrack || track.id === targetTrack?.id);
      if (!hasTrack && targetTrack) {
        try {
          stream.addTrack(targetTrack);
        } catch (error) {
          // ignore duplicate track errors
        }
      }
    },
    detach() {
      const el = this.$refs.videoEl;
      if (!el) {
        return;
      }
      if (this.boundTrack && typeof this.boundTrack.detach === "function") {
        try {
          this.boundTrack.detach(el);
        } catch (error) {
          // ignore detach errors
        }
      }
      this.boundTrack = null;
      el.srcObject = null;
    },
    applyMuted() {
      const el = this.$refs.videoEl;
      if (!el) {
        return;
      }
      el.muted = Boolean(this.isMuted);
    },
    applySinkId() {
      const el = this.$refs.videoEl;
      if (!el || !this.sinkId || typeof el.setSinkId !== "function") {
        return;
      }
      el.setSinkId(this.sinkId).catch(() => {});
    },
  },
};
</script>

<!--
  XXX: The LAST raw CSS in the video-engine, and deliberately so. This component
  wraps a bare <video> element: iCL has no primitive for one, because a
  MediaStreamTrack has to be attached to a real DOM node. The letterbox colour
  and the mirror transform belong to that element, not to a themeable surface.
  Everything around it is iCL -- see iVideoTile for the chrome.
-->
<style scoped>
.media-video {
  width: 100%;
  height: 100%;
  background: #000;
}

.video-cover {
  object-fit: cover;
}

.video-flipped {
  transform: scaleX(-1);
}
</style>
